import React, { FC, useState } from 'react'
import { Box, Stack, Typography } from '@mui/material'

import {
  NotionButton,
  NotionSelect,
  NotionTextField,
  TabRow,
} from '@/apps/latest/components/portal/notion'
import { PORTAL_TEXT } from '@/apps/latest/shared/styles/portalTypography'
import {
  SoapRule,
  SoapRuleKind,
  SoapSection,
  soapSectionLabels,
} from '@/features/internal/soap-rules/types/soapRule'

type Props = {
  rule?: SoapRule
  onSubmit: (rule: Omit<SoapRule, 'id' | 'created_at'>) => void
  onCancel: () => void
  isLoading?: boolean
}

const KIND_TABS: { id: SoapRuleKind; label: string }[] = [
  { id: 'add', label: '追記' },
  { id: 'rewrite', label: '置換' },
]

const SECTION_OPTIONS = Object.entries(soapSectionLabels).map(
  ([value, label]) => ({
    value,
    label,
  })
)

export const SoapRuleForm: FC<Props> = ({
  rule,
  onSubmit,
  onCancel,
  isLoading = false,
}) => {
  const [kind, setKind] = useState<SoapRuleKind>(rule?.kind ?? 'add')
  const [whenText, setWhenText] = useState(rule?.when_text ?? '')
  const [thenText, setThenText] = useState(rule?.then_text ?? '')
  const [section, setSection] = useState<SoapSection>(
    rule?.section || 'educational_plans'
  )
  const [errors, setErrors] = useState<{ [key: string]: string }>({})

  const handleSubmit = (e: React.FormEvent) => {
    e.preventDefault()
    const newErrors: { [key: string]: string } = {}
    if (!whenText.trim()) {
      newErrors.when_text =
        kind === 'add' ? '会話キーワードは必須です' : '置換対象は必須です'
    }
    if (kind === 'add' && !thenText.trim()) {
      newErrors.then_text = '追記する文言は必須です'
    }
    setErrors(newErrors)
    if (Object.keys(newErrors).length > 0) return

    onSubmit({
      kind,
      when_text: whenText.trim(),
      // rewrite は前後の空白も置換対象・置換後の一部になり得るため trim しない
      then_text: kind === 'add' ? thenText.trim() : thenText,
      section: kind === 'add' ? section : '',
    })
  }

  return (
    <Box component="form" onSubmit={handleSubmit}>
      <Stack gap={2}>
        <TabRow<SoapRuleKind>
          tabs={KIND_TABS}
          active={kind}
          onChange={setKind}
        />
        <Typography sx={PORTAL_TEXT.cellMeta}>
          {kind === 'add'
            ? '会話にキーワードが出たら、指定の欄に文言を追記します'
            : 'SOAP全体の文字列を置換します（置換後を空欄にすると削除）'}
        </Typography>

        <NotionTextField
          label={kind === 'add' ? '会話キーワード' : '置換対象'}
          value={whenText}
          onChange={(e) => setWhenText(e.target.value)}
          error={!!errors.when_text}
          helperText={errors.when_text}
          placeholder={kind === 'add' ? '例：ジェネリック' : '例：Do処方'}
        />

        {kind === 'add' && (
          <NotionSelect<SoapSection>
            label="追記先"
            value={section}
            onChange={setSection}
            options={SECTION_OPTIONS as { value: SoapSection; label: string }[]}
            fullWidth
          />
        )}

        <NotionTextField
          label={kind === 'add' ? '追記する文言' : '置換後（空欄で削除）'}
          value={thenText}
          onChange={(e) => setThenText(e.target.value)}
          error={!!errors.then_text}
          helperText={errors.then_text}
          placeholder={
            kind === 'add' ? '例：後発品の変更について説明した。' : '例：同処方'
          }
          multiline
          rows={kind === 'add' ? 3 : 2}
        />

        <Box display="flex" gap={2} justifyContent="flex-end">
          <NotionButton variant="secondary" onClick={onCancel}>
            キャンセル
          </NotionButton>
          <NotionButton type="submit" variant="primary" disabled={isLoading}>
            {isLoading ? '処理中...' : rule ? '更新' : '作成'}
          </NotionButton>
        </Box>
      </Stack>
    </Box>
  )
}
